<script setup lang="ts">
import { getAgencyDisplayName } from "~/components/agency/agency.model";
import { useTableColumn } from "~/composables/useTableColumn";
import { getWantedProfileStatusColor } from "~/components/wanted-profile/wanted-profile.model";
import { useWantedProfileList } from "~/components/wanted-profile/list/useWantedProfileList";

const props = defineProps<{ companyId: string }>();

const { t } = useI18n();
const {
  filters,
  wantedProfiles,
  totalItems,
  statusItems,
  getStatusLabel,
  qualificationItems,
  getQualificationLabel,
  isLoading,
  emptyStateType,
  isSheetOpen,
  wantedProfileToEdit,
  openCreateSheet,
  closeSheet,
  handleSheetSaved,
  openEditSheet,
  getPrimaryRowAction,
  getSecondaryRowActions,
  setStatusFilter,
  setEmployeeStatusFilter,
  resetFilters,
} = useWantedProfileList(() => props.companyId);

const statusLabel = t("wantedProfiles.columns.status");
const jobLabel = t("wantedProfiles.columns.job");
const agencyLabel = t("wantedProfiles.columns.agency");
const qualificationLabel = t("wantedProfiles.columns.qualification");

const { tableKey, tableColumns } = useTableColumn(
  [
    {
      label: statusLabel,
      value: { accessorKey: "status", header: statusLabel, maxSize: 60 },
      blocked: true,
      selected: true,
    },
    { label: jobLabel, value: { accessorKey: "job", header: jobLabel, minSize: 300 }, blocked: true, selected: true },
    { label: agencyLabel, value: { accessorKey: "agency", header: agencyLabel }, blocked: true, selected: true },
    {
      label: qualificationLabel,
      value: { accessorKey: "employee_status", header: qualificationLabel },
      blocked: true,
      selected: true,
    },
  ],
  "wanted-profiles-table-columns"
);

const ui = {
  root: "flex flex-1 flex-col h-full",
  jobLabel: "font-medium",
  description: "text-secondary-800",
  createButton: "ml-auto",
  agencyFilter: "w-56",
};
</script>

<template>
  <div :class="ui.root">
    <TableFiltersContainer padding="2px 0px 12px 0px">
      <AwTextField
        v-model="filters.search"
        leading-icon="i-ph-magnifying-glass"
        :placeholder="$t('wantedProfiles.list.searchPlaceholder')"
        class="min-w-fit"
        :debounce-delay="200"
      />
      <TableStatusSelect
        :status="filters.status"
        :title="$t('wantedProfiles.columns.status')"
        :status-options="statusItems"
        @update:status="setStatusFilter"
      />
      <TableStatusSelect
        :status="filters.employeeStatus"
        :title="$t('wantedProfiles.columns.qualification')"
        :status-options="qualificationItems"
        @update:status="setEmployeeStatusFilter"
      />
      <div :class="ui.agencyFilter">
        <AgencySelectFieldSingle
          v-model:agency-id="filters.agencyId"
          :company-id="props.companyId"
          content-class="min-w-fit"
        />
      </div>
      <TableFiltersResetButton @click="resetFilters" />
      <AwButton :class="ui.createButton" @click="openCreateSheet">
        <AwIcon name="i-ph-plus" />
        {{ $t("wantedProfiles.list.createButton") }}
      </AwButton>
    </TableFiltersContainer>
    <TableInteractive
      :key="tableKey"
      :data="wantedProfiles"
      :columns="tableColumns"
      :loading="isLoading"
      :empty-state-type="emptyStateType"
      :empty-state-reset-filters="resetFilters"
      height="100%"
      :on-select="(row) => !isLoading && openEditSheet(row.original)"
    >
      <template #status-cell="{ row }">
        <TableCellContainer :is-loading="isLoading" :skeletons="['status']">
          <AwBadge :color="getWantedProfileStatusColor(row.original.status)">
            {{ getStatusLabel(row.original.status) }}
          </AwBadge>
        </TableCellContainer>
      </template>
      <template #job-cell="{ row }">
        <TableCellContainer :is-loading="isLoading" :skeletons="['text', 'text']">
          <div>
            <div :class="ui.jobLabel">{{ row.original.job.label }}</div>
            <TextEllipsis :class="ui.description">
              {{ row.original.description ?? $t("wantedProfiles.list.noComment") }}
            </TextEllipsis>
          </div>
        </TableCellContainer>
      </template>
      <template #agency-cell="{ row }">
        <TableCellContainer :is-loading="isLoading" :skeletons="['text']">
          <AwBadge color="gray">{{
            getAgencyDisplayName(row.original.agency.name, row.original.agency.postal_code)
          }}</AwBadge>
        </TableCellContainer>
      </template>
      <template #employee_status-cell="{ row }">
        <TableCellContainer :is-loading="isLoading" :skeletons="['text']">
          {{ getQualificationLabel(row.original.employee_status) }}
        </TableCellContainer>
      </template>
      <template #primaryAction="{ row }">
        <TablePrimaryAction v-if="!isLoading" :action="getPrimaryRowAction(row.original)" />
      </template>
      <template #actions="{ row }">
        <TableRowActions v-if="!isLoading" :row="row" :actions="getSecondaryRowActions(row.original)" />
      </template>
      <template #footer>
        <TablePagination
          :total-items="totalItems"
          :page-size="filters.pageSize"
          :page-index="filters.pageIndex"
          @update:page-size="filters.pageSize = $event"
          @update:page-index="filters.pageIndex = $event"
        />
      </template>
    </TableInteractive>

    <LazyWantedProfileSheet
      v-if="isSheetOpen"
      :company-id="props.companyId"
      :wanted-profile="wantedProfileToEdit"
      @on-saved="handleSheetSaved"
      @on-close="closeSheet"
    />
  </div>
</template>
